import type { CheckboxChangeEventType } from '@myosh/odin-components';
import { isArray } from 'lodash';
import type {
  HierarchyDropdownValue,
  HierarchyType,
  TransformedFormHierarchyTypes,
} from '../../../@types/hierarchy-fields';
import { forceAssert } from '../../../utils/type-guards';
import type { RecordHierarchyFields } from './hierarchy-field-group.component';
import { hierarchyDeselectValue } from './hierarchy-field.component';
import {
  getHierarchyFieldValue,
  getInitialHierarchyFieldsConfigurations,
  mergeIgnoreHierarchyAccess,
} from './hierarchy-functions';

export type FieldValues = Record<string, HierarchyDropdownValue | HierarchyDropdownValue[]>;

interface HierarchyFieldsState {
  fieldsValues?: FieldValues;
  dependentHierarchyTypes?: Record<string, string>;
  hierarchyFieldsState?: Array<RecordHierarchyFields>;
}

export const HierarchiesActionType = {
  SetInitialFieldsConfigurations: 0,
  SetInitialFieldsValue: 1,
  UpdateFieldValue: 2,
  SetIgnoreHIerarchyAccess: 3,
} as const;
export type HierarchiesActionType = (typeof HierarchiesActionType)[keyof typeof HierarchiesActionType];

interface HierarchyReducerAction {
  type: HierarchiesActionType;
  hierachyTypesAll?: Array<HierarchyType>;
  hierarchyTypes?: Array<TransformedFormHierarchyTypes | HierarchyType>;
  value?: Record<string, HierarchyDropdownValue | HierarchyDropdownValue[]>;
  hierarchyField?: RecordHierarchyFields;
  fieldValue?: HierarchyDropdownValue | HierarchyDropdownValue[];
  isUserHierarchy?: boolean;
  isCompetencyHierarchy?: boolean;
  isExternal?: boolean;
  ignoreHierarchyAccess?: CheckboxChangeEventType;
  usesMultiSelectDropdowns?: boolean;
  isAdvancedHierarchiesEnabled?: boolean;
  isNewRecord?: boolean;
  allHierarchyValues?: Array<HierarchyDropdownValue>;
  isTreeHierarchy?: boolean;
}

export const HierarchiesReducer = (
  state: HierarchyFieldsState,
  action: HierarchyReducerAction
): HierarchyFieldsState => {
  const {
    type,
    value = {},
    hierarchyTypes,
    hierachyTypesAll,
    hierarchyField,
    fieldValue,
    isUserHierarchy,
    isCompetencyHierarchy,
    isExternal,
    ignoreHierarchyAccess,
    usesMultiSelectDropdowns,
    isAdvancedHierarchiesEnabled,
    isNewRecord,
    allHierarchyValues,
    isTreeHierarchy = false,
  } = action;
  const { fieldsValues, dependentHierarchyTypes = {}, hierarchyFieldsState } = state;

  switch (type) {
    case HierarchiesActionType.SetInitialFieldsConfigurations:
      if (hierarchyTypes && hierachyTypesAll) {
        const fieldsConfiguration = getInitialHierarchyFieldsConfigurations(
          hierarchyTypes,
          hierachyTypesAll,
          isUserHierarchy,
          isCompetencyHierarchy,
          isExternal,
          isTreeHierarchy
        );
        return { ...state, ...fieldsConfiguration };
      }
      return state;

    case HierarchiesActionType.SetInitialFieldsValue: {
      const initialFieldValues: Record<string, HierarchyDropdownValue | HierarchyDropdownValue[]> = {};

      if (hierarchyFieldsState) {
        for (let i = 0; i < hierarchyFieldsState.length; i++) {
          const field = hierarchyFieldsState[i];
          const fieldValue = isArray(value) ? value.map((item) => item[field.caption]) : value[field.caption];
          const useInitialValue = isNewRecord ? field.hierarchyTypeConfigDTO?.useInitialValue : true;
          initialFieldValues[field.caption] = forceAssert(
            getHierarchyFieldValue(
              value,
              field,
              fieldValue,
              dependentHierarchyTypes,
              usesMultiSelectDropdowns,
              useInitialValue
            )
          );
        }
      }
      return {
        ...state,
        fieldsValues: mergeIgnoreHierarchyAccess(initialFieldValues, fieldsValues),
      };
    }

    case HierarchiesActionType.UpdateFieldValue: {
      if (!hierarchyField || !fieldsValues || !fieldValue || !hierarchyFieldsState) {
        return state;
      }

      let updatedState: Record<string, HierarchyDropdownValue | HierarchyDropdownValue[]>;

      // Advanced hierarchies with showAll enabled
      if (
        isAdvancedHierarchiesEnabled &&
        hierarchyField.hierarchyTypeConfigDTO?.showAll &&
        !isArray(fieldValue) &&
        allHierarchyValues
      ) {
        // Build hierarchy chain from bottom up
        let currentParentId = fieldValue?.parentId;
        let currentParentCaption = dependentHierarchyTypes[hierarchyField.caption];

        updatedState = {
          ...fieldsValues,
          [hierarchyField.caption]: { ...fieldValue, selectedParentId: fieldValue.parentId },
        };

        // If the field is configured to show all and a value is selected, auto-populate all related hierarchy fields to maintain valid parent-child relationships
        while (currentParentId) {
          const parentFieldValue = allHierarchyValues.find((item) => item.id === currentParentId);
          if (!parentFieldValue) break;

          updatedState[currentParentCaption] = {
            ...parentFieldValue,
            selectedParentId: parentFieldValue.parentId,
          };

          currentParentId = parentFieldValue.parentId;
          currentParentCaption = dependentHierarchyTypes[currentParentCaption];
        }
      } else {
        updatedState = {
          ...fieldsValues,
          [hierarchyField.caption]: forceAssert(
            getHierarchyFieldValue(
              fieldsValues,
              hierarchyField,
              fieldValue,
              dependentHierarchyTypes,
              usesMultiSelectDropdowns
            )
          ),
        };
      }

      if (!usesMultiSelectDropdowns) {
        for (const key in dependentHierarchyTypes) {
          const currentValue = updatedState[key];
          const parentValue = updatedState[dependentHierarchyTypes[key]];

          if (isArray(currentValue) || isArray(parentValue)) continue;

          if (currentValue?.parentId !== parentValue?.id || parentValue?.id === undefined) {
            updatedState[key] = forceAssert({
              ...hierarchyDeselectValue,
              selectedParentId: parentValue?.id,
            });
          }
        }
      }

      return {
        ...state,
        fieldsValues: mergeIgnoreHierarchyAccess(updatedState, fieldsValues),
      };
    }

    case HierarchiesActionType.SetIgnoreHIerarchyAccess: {
      const updatedFieldValues = {
        ...fieldsValues,
        ignoreHierarchyAccess: ignoreHierarchyAccess,
      };

      return {
        ...state,
        fieldsValues: forceAssert(updatedFieldValues),
      };
    }

    default:
      return state;
  }
};
